import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Checkbox,
  Text,
  Stack,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import {
  useForm,
  SubmitHandler,
  UseFormReturn as RHFUseFormReturn,
} from 'react-hook-form';

// Re-export the UseFormReturn type
export type UseFormReturn<
  T extends Record<string, unknown> = Record<string, unknown>
> = RHFUseFormReturn<T>;

import { FloatingLabelInput } from './FloatingLabelInput';
import validators from '~/lib/forms/rhf-validators';
import { ROUTES } from '@habity/constants';
import { useTranslations } from 'next-intl';
import { PageFooter } from '../page/footer';
import { Show } from '@chakra-ui/react';

export interface SignupFormValues extends Record<string, unknown> {
  name: string;
  email: string;
  phone: string;
  acceptTerms: boolean;
  acceptPrivacyPolicy: boolean;
  loanContactRequest?: boolean;
  valuationRequest?: boolean;
}

export interface SignupFormProps {
  onSubmit: SubmitHandler<SignupFormValues>;
  defaultValues?: Partial<SignupFormValues>;
  isLoading?: boolean;
  submitButtonLabel?: string;
  showNameField?: boolean;
  showPhoneField?: boolean;
  disabledFields?: {
    name?: boolean;
    email?: boolean;
    phone?: boolean;
  };
  renderAfterForm?: React.ReactNode;
  renderControls?: (
    formMethods: UseFormReturn<SignupFormValues>
  ) => React.ReactNode;
  onCancel?: () => void;
  formRef?: React.RefObject<UseFormReturn<SignupFormValues> | null>;
  /** Whether to use sticky footer on mobile (e.g., in seller onboarding) */
  useStickyFooterOnMobile?: boolean;
  /** Whether to show the valuation request checkbox */
  showValuationCheckbox?: boolean;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  onSubmit,
  defaultValues = {},
  isLoading = false,
  submitButtonLabel = 'Skicka',
  showNameField = true,
  showPhoneField = true,
  disabledFields = {},
  renderAfterForm,
  renderControls,
  onCancel,
  formRef,
  useStickyFooterOnMobile = false,
  showValuationCheckbox = true,
}) => {
  // Use translation hooks
  const t = useTranslations('Forms.Signup');

  const methods = useForm<SignupFormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      acceptTerms: false,
      acceptPrivacyPolicy: false,
      loanContactRequest: false,
      valuationRequest: false,
      ...defaultValues,
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = methods;

  // Initialize watch function for potential future use
  watch();

  // If formRef is provided, expose form methods to parent component
  React.useEffect(() => {
    if (formRef && 'current' in formRef) {
      (
        formRef as React.MutableRefObject<UseFormReturn<SignupFormValues>>
      ).current = methods;
    }
  }, [formRef, methods]);

  const actualLoading = isLoading || isSubmitting;

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      rounded="md"
      backgroundColor="white"
    >
      <Stack spacing="4">
        {showNameField && (
          <FloatingLabelInput
            label={t('name.label')}
            placeholder={t('name.placeholder')}
            isInvalid={!!errors.name}
            error={errors.name?.message}
            {...register('name', validators.requiredFullName())}
            defaultValue={defaultValues?.name}
            isDisabled={actualLoading || disabledFields.name}
            isRequired
            useFloatingLabel
            autoFocus
          />
        )}

        <FloatingLabelInput
          label={t('email.label')}
          placeholder={t('email.placeholder')}
          isInvalid={!!errors.email}
          error={errors.email?.message}
          {...register('email', validators.email())}
          defaultValue={defaultValues?.email}
          isDisabled={actualLoading || disabledFields.email}
          isRequired
          useFloatingLabel
        />

        {showPhoneField && (
          <>
            <FloatingLabelInput
              label={t('phone.label')}
              placeholder={t('phone.placeholder')}
              isInvalid={!!errors.phone}
              error={errors.phone?.message}
              {...register('phone', validators.requiredMobilePhone())}
              defaultValue={defaultValues?.phone}
              isDisabled={actualLoading || disabledFields.phone}
              isRequired
              useFloatingLabel
            />
          </>
        )}
        <FormControl isInvalid={!!errors.acceptTerms}>
          <Box maxW="260px">
            <Checkbox
              {...register(
                'acceptTerms',
                validators.mustBeTrue(
                  'Du måste godkänna villkoren för att fortsätta'
                )
              )}
              onChange={(e) => {
                const checked = e.target.checked;
                setValue('acceptTerms', checked);
                setValue('acceptPrivacyPolicy', checked);
              }}
              isDisabled={actualLoading}
              defaultChecked={defaultValues.acceptTerms || false}
              borderColor="blackAlpha.500"
              size="lg"
              alignItems="flex-start"
              spacing="3"
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                color="blackAlpha.700"
                lineHeight="short"
                mt="-1px"
              >
                Jag godkänner{' '}
                <Link
                  href={ROUTES.CMS.TERMS}
                  target="_blank"
                  color="blackAlpha.700"
                  textDecoration="underline"
                >
                  användarvillkoren
                </Link>{' '}
                och{' '}
                <Link
                  href={ROUTES.CMS.PRIVACY}
                  target="_blank"
                  color="blackAlpha.700"
                  textDecoration="underline"
                >
                  integritetspolicyn
                </Link>
              </Text>
            </Checkbox>
          </Box>
          <FormErrorMessage fontSize="xs">
            {errors.acceptTerms?.message}
          </FormErrorMessage>
        </FormControl>

        {showValuationCheckbox && (
          <FormControl>
            <Box
              bg="orange.50"
              p={{ base: 3, md: 3 }}
              borderRadius="md"
              border="1px solid"
              borderColor="orange.200"
            >
              <VStack align="stretch" spacing={2}>
                <Text
                  fontSize={{ base: 'xs', md: 'xs' }}
                  fontWeight="semibold"
                  color="gray.800"
                >
                  Har du en bostad att värdera?
                </Text>
                <VStack align="stretch" spacing={2}>
                  <Checkbox
                    {...register('valuationRequest')}
                    isDisabled={actualLoading}
                    defaultChecked={defaultValues.valuationRequest || false}
                    borderColor="blackAlpha.500"
                  >
                    <Text
                      size={{ base: 'xs', md: 'xs' }}
                      color="blackAlpha.900"
                    >
                      Ja, mäklaren får gärna kontakta mig för en värdering.
                    </Text>
                  </Checkbox>
                </VStack>
              </VStack>
            </Box>
          </FormControl>
        )}

        <FormControl>
          <Box
            bg="yellow.50"
            p={{ base: 3, md: 3 }}
            borderRadius="md"
            border="1px solid"
            borderColor="yellow.200"
          >
            <VStack align="stretch" spacing={2}>
              <Text
                fontSize={{ base: 'xs', md: 'xs' }}
                fontWeight="semibold"
                color="gray.800"
              >
                Behöver du ett lånelöfte till visningen?
              </Text>
              <VStack align="stretch" spacing={2}>
                <Checkbox
                  {...register('loanContactRequest')}
                  isDisabled={actualLoading}
                  defaultChecked={defaultValues.loanContactRequest || false}
                  borderColor="blackAlpha.500"
                >
                  <Text size={{ base: 'xs', md: 'xs' }} color="blackAlpha.900">
                    Ja, Söderberg & Partners Bolån får gärna kontakta mig.
                  </Text>
                </Checkbox>
              </VStack>
            </VStack>
          </Box>
        </FormControl>

        {renderControls ? (
          renderControls(methods)
        ) : useStickyFooterOnMobile ? (
          <>
            {/* Desktop buttons */}
            <Show above="tablet">
              {onCancel && (
                <Button
                  variant="outline"
                  width="full"
                  onClick={onCancel}
                  isDisabled={actualLoading}
                  mt={2}
                >
                  Avbryt
                </Button>
              )}
              <Button
                type="submit"
                width="full"
                isLoading={actualLoading}
                mt={2}
                bg="blackAlpha.900"
                color="white"
                _hover={{ bg: 'blackAlpha.700' }}
              >
                {submitButtonLabel}
              </Button>
            </Show>

            {/* Mobile buttons - stick to bottom */}
            <Show below="tablet">
              <PageFooter>
                <Stack direction="row" spacing="3" width="100%">
                  {onCancel && (
                    <Button
                      variant="outline"
                      flex="1"
                      onClick={onCancel}
                      isDisabled={actualLoading}
                    >
                      Avbryt
                    </Button>
                  )}
                  <Button
                    type="submit"
                    flex="1"
                    isLoading={actualLoading}
                    bg="blackAlpha.900"
                    color="white"
                    _hover={{ bg: 'blackAlpha.700' }}
                  >
                    {submitButtonLabel}
                  </Button>
                </Stack>
              </PageFooter>
            </Show>
          </>
        ) : (
          <>
            {onCancel && (
              <Button
                variant="outline"
                width="full"
                onClick={onCancel}
                isDisabled={actualLoading}
                mt={2}
              >
                Avbryt
              </Button>
            )}
            <Button
              type="submit"
              width="full"
              isLoading={actualLoading}
              mt={2}
              bg="blackAlpha.900"
              color="white"
              _hover={{ bg: 'blackAlpha.700' }}
            >
              {submitButtonLabel}
            </Button>
          </>
        )}

        {renderAfterForm}
      </Stack>
    </Box>
  );
};
